import React from "react";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type FlightFareSelectionFunction,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FlightCardDetailsLoading,
  FlightCardRecheckBaggageBanner,
  FlightCardSummary,
  FlightCardWrapper,
  FlightDetails,
  FlightDetailsDialog,
  FlightFareGrid,
  useFlightCard,
  type UseFlightCardParams,
} from "@tenant/flights";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import { useFlightSlot } from "../../../../slots";
import {
  FlightRawEventName,
  useEmitCoreFlightRawEventOnce,
} from "../../../../tracking";
import defaultStyles from "./FlightCard.module.scss";

export interface FlightCardComponentProps
  extends PlatformComponentProps,
    UseFlightCardParams {
  onFareSelect: FlightFareSelectionFunction;
  goToNextStep: () => void;
  index?: number;
}

export const FlightCard = React.forwardRef<
  HTMLDivElement,
  FlightCardComponentProps
>((props, forwardedRef) => {
  const {
    flight,
    isSelected,
    sliceIndex = 0,
    onFareSelect,
    goToNextStep,
    children,
    className,
  } = props;
  const COMPONENT_KEY = CoreFlightsComponent.FlightCard;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const FlightSlot = useFlightSlot();

  const { t } = useI18nContext();

  const {
    context: {
      isFlightDetailsLoading,
      open,
      flightCardSummary,
      fareData,
      tripCategory,
      fareShelfFilter,
      flightDetails,
      flightSlice,
      fareShelfFilterWithUpsell,
      openFlightDetailsDialog,
      hasSelfTransferLayover,
    },
    handlers: {
      handleFlightCardClick,
      setOpenFlightDetailsDialog,
      handleFareGridSelection,
    },
  } = useFlightCard(props);

  useEmitCoreFlightRawEventOnce({ name: FlightRawEventName.ViewedSlice }, open);

  return (
    <div className={clsx(block, className)} ref={forwardedRef}>
      <FlightCardWrapper
        className={cn("button")}
        onClick={handleFlightCardClick}
      >
        {hasSelfTransferLayover ? (
          <FlightCardRecheckBaggageBanner
            className={cn("vi-banner")}
            flightSlice={flightSlice}
          />
        ) : null}
        {flightCardSummary == null ? null : (
          <div className={cn("summary")}>
            <FlightCardSummary {...flightCardSummary} />
          </div>
        )}
        <div className={cn("fare-grid")}>
          <FlightFareGrid
            fares={fareData}
            tripType={tripCategory}
            onFareGridClick={handleFareGridSelection}
            fareShelfFilter={fareShelfFilter}
          />
        </div>
        <Icon
          iconName={IconNameEnum.dropdownTriangle}
          className={clsx(
            cn("icon", {
              expanded: isSelected,
            })
          )}
        />
      </FlightCardWrapper>

      {open && (
        <>
          <div
            className={cn("flight-card-details", {
              open: open,
              loading: isFlightDetailsLoading,
            })}
          >
            {/* Conditional rendering for flight details based on loading state */}
            {isFlightDetailsLoading ||
            flightDetails == null ||
            flightSlice == null ? (
              <FlightCardDetailsLoading />
            ) : (
              <>
                <div className={cn("flight-details")}>
                  <FlightDetails
                    flightDetails={flightDetails}
                    flightSlice={flightSlice}
                    planeInfo="" // TODO: Update plane info for segments
                    isOutgoing={sliceIndex === 0}
                    classNames="" // TODO: Add appropriate classNames
                  />
                </div>
                <div className={cn("fare-details")}>
                  <FlightSlot.FlightCardFareList
                    trip={flightDetails}
                    fareIdList={flight.fares.map((f) => {
                      return "example" in f ? f.fareSlice : f.id;
                    })}
                    fareDetails={flightDetails.fareDetails}
                    sliceIndex={sliceIndex}
                    fareShelfFilter={fareShelfFilterWithUpsell}
                    sliceId={flight.sliceId}
                    tripId={flightDetails.id}
                    onFareSelect={onFareSelect}
                    goToNextStep={goToNextStep}
                  />
                </div>
              </>
            )}
          </div>
          <div className={cn("bottom-floating-root", { fixed: true })}>
            <button
              className={cn("continue-button")}
              onClick={() => {
                setOpenFlightDetailsDialog(true);
              }}
              disabled={isFlightDetailsLoading}
            >
              {t("continue")}
            </button>
          </div>
        </>
      )}
      <FlightDetailsDialog
        open={openFlightDetailsDialog}
        onClose={() => setOpenFlightDetailsDialog(false)}
        onClick={() => {
          goToNextStep();
        }}
        flightSlice={flightSlice}
        isOutbound={sliceIndex === 0}
      />
      {children}
    </div>
  );
});
